import api from "@/services/api";
import type {
  AuthProvider,
  AuthSession,
  ForgotPasswordRequest,
  ForgotPasswordVerify,
  GoogleLoginResult,
  PasswordResetChallenge,
  PasswordResetResult,
  PasswordResetVerification,
  PhoneOtpChallenge,
  PhoneOtpRequest,
  PhoneOtpVerification,
  RegisterInitPayload,
  RegisterVerifyPayload,
  ResetPasswordPayload,
  SetPasswordPayload,
} from "@/types";
import {
  buildForgotPasswordPayload,
  buildLoginPayload,
  buildPhoneOtpPayload,
  buildResetPasswordPayload,
  maskAccount,
} from "@/utils/auth-account";
import { type UnknownRecord, unwrapData } from "@/utils/normalize";
import { getStoredRefreshToken } from "@/utils/auth-storage";

import { normalizeTenant, normalizeUser } from "./auth.shared";

const normalizeSession = (payload: UnknownRecord): AuthSession => {
  const rawData = unwrapData<UnknownRecord>(payload);
  const rawTokens =
    typeof rawData.tokens === "object" && rawData.tokens !== null
      ? (rawData.tokens as UnknownRecord)
      : rawData;

  const accessToken = String(
    rawTokens.accessToken ??
      rawTokens.access_token ??
      rawData.accessToken ??
      rawData.access_token ??
      "",
  );

  if (!accessToken) {
    throw new Error("Không lấy được phiên đăng nhập hợp lệ.");
  }

  return {
    user: normalizeUser(
      typeof rawData.user === "object" && rawData.user !== null
        ? (rawData.user as UnknownRecord)
        : undefined,
    ),
    tenant: normalizeTenant(
      typeof rawData.tenant === "object" && rawData.tenant !== null
        ? (rawData.tenant as UnknownRecord)
        : undefined,
    ),
    accessToken,
    refreshToken:
      typeof rawTokens.refreshToken === "string"
        ? rawTokens.refreshToken
        : typeof rawTokens.refresh_token === "string"
          ? rawTokens.refresh_token
          : typeof rawData.refreshToken === "string"
            ? rawData.refreshToken
            : typeof rawData.refresh_token === "string"
              ? rawData.refresh_token
              : undefined,
    expiresIn:
      typeof rawTokens.expiresIn === "number"
        ? rawTokens.expiresIn
        : typeof rawTokens.expires_in === "number"
          ? rawTokens.expires_in
          : undefined,
  };
};

const buildPhoneOtpChallenge = (
  phone: string,
  payload: unknown,
): PhoneOtpChallenge => {
  const rawData = unwrapData<UnknownRecord>((payload ?? {}) as UnknownRecord);
  const expiresAt =
    typeof rawData.expired_at === "string"
      ? rawData.expired_at
      : new Date(Date.now() + 10 * 60_000).toISOString();

  return {
    phone,
    maskedPhone: maskAccount(phone),
    expiresAt,
    sessionId: typeof rawData.session_id === "string" ? rawData.session_id : "",
  };
};

const buildPasswordResetChallenge = (
  account: string,
  payload: unknown,
): PasswordResetChallenge => {
  const rawData = unwrapData<UnknownRecord>((payload ?? {}) as UnknownRecord);
  const expiresAt =
    typeof rawData.expired_at === "string"
      ? rawData.expired_at
      : new Date(Date.now() + 10 * 60_000).toISOString();

  return {
    account,
    maskedAccount: maskAccount(account),
    expiresAt,
  };
};

export const apiAuthProvider: AuthProvider = {
  async loginWithPassword(credentials) {
    const { data } = await api.post("/auth/login", {
      ...buildLoginPayload(credentials),
      ...(credentials.recaptchaToken
        ? { recaptcha_token: credentials.recaptchaToken }
        : {}),
    });
    return normalizeSession(data as UnknownRecord);
  },

  async sendLoginOtp(payload: PhoneOtpRequest) {
    const { phone } = buildPhoneOtpPayload(payload.phone);
    const { data } = await api.post("/auth/otp/init", {
      phone,
      ...(payload.recaptchaToken
        ? { recaptcha_token: payload.recaptchaToken }
        : {}),
    });
    return buildPhoneOtpChallenge(phone, data);
  },

  async resendLoginOtp(payload: PhoneOtpRequest) {
    const { phone } = buildPhoneOtpPayload(payload.phone);
    const { data } = await api.post("/auth/otp/resend", {
      session_id: payload.sessionId,
      ...(payload.recaptchaToken
        ? { recaptcha_token: payload.recaptchaToken }
        : {}),
    });
    return buildPhoneOtpChallenge(phone, data);
  },

  async verifyLoginOtp(payload: PhoneOtpVerification) {
    const { data } = await api.post("/auth/otp/verify", {
      session_id: payload.sessionId,
      otp: payload.otp,
      ...(payload.recaptchaToken
        ? { recaptcha_token: payload.recaptchaToken }
        : {}),
    });
    return normalizeSession(data as UnknownRecord);
  },

  async loginWithGoogle(accessToken: string): Promise<GoogleLoginResult> {
    const { data } = await api.post("/auth/login/google", {
      token: accessToken,
    });
    const rawData = unwrapData<UnknownRecord>(data as UnknownRecord);

    if (
      rawData.phone_required === true &&
      typeof rawData.pending_token === "string"
    ) {
      return {
        phoneRequired: true,
        pendingToken: rawData.pending_token,
        email: typeof rawData.email === "string" ? rawData.email : undefined,
        name: typeof rawData.name === "string" ? rawData.name : undefined,
        avatar: typeof rawData.avatar === "string" ? rawData.avatar : undefined,
      };
    }

    return {
      phoneRequired: false,
      session: normalizeSession(data as UnknownRecord),
    };
  },

  async handleSocialCallback(
    provider: string,
    code: string,
    state: string,
  ): Promise<AuthSession> {
    const { data } = await api.get(`/auth/social/${provider}/callback`, {
      params: { code, state },
    });
    return normalizeSession(data as UnknownRecord);
  },

  async sendGooglePhoneOtp(payload) {
    const { phone } = buildPhoneOtpPayload(payload.phone);
    const { data } = await api.post("/auth/phone/send-otp", {
      phone,
      pending_token: payload.pendingToken,
      ...(payload.recaptchaToken
        ? { recaptcha_token: payload.recaptchaToken }
        : {}),
    });
    return buildPhoneOtpChallenge(phone, data);
  },

  async verifyGooglePhone(payload) {
    const { phone } = buildPhoneOtpPayload(payload.phone);
    const { data } = await api.post("/auth/phone/verify", {
      phone,
      otp: payload.otp,
      pending_token: payload.pendingToken,
      ...(payload.recaptchaToken
        ? { recaptcha_token: payload.recaptchaToken }
        : {}),
    });
    return normalizeSession(data as UnknownRecord);
  },

  async refresh() {
    const refreshToken = getStoredRefreshToken();
    const { data } = await api.post("/auth/refresh", {
      refresh_token: refreshToken || undefined,
    });
    const rawData = unwrapData<UnknownRecord>(data as UnknownRecord);
    const rawTokens =
      typeof rawData.tokens === "object" && rawData.tokens !== null
        ? (rawData.tokens as UnknownRecord)
        : rawData;

    return {
      accessToken: String(
        rawTokens.accessToken ??
          rawTokens.access_token ??
          rawData.accessToken ??
          rawData.access_token ??
          "",
      ),
      refreshToken:
        typeof rawTokens.refreshToken === "string"
          ? rawTokens.refreshToken
          : typeof rawTokens.refresh_token === "string"
            ? rawTokens.refresh_token
            : undefined,
      expiresIn:
        typeof rawTokens.expiresIn === "number"
          ? rawTokens.expiresIn
          : typeof rawTokens.expires_in === "number"
            ? rawTokens.expires_in
            : undefined,
    };
  },

  async getProfile() {
    const { data } = await api.get("/profile");
    const rawData = unwrapData<UnknownRecord>(data as UnknownRecord);

    return {
      user: normalizeUser(rawData.user as UnknownRecord | undefined),
      tenant: normalizeTenant(rawData.tenant as UnknownRecord | undefined),
    };
  },

  async logout() {
    await api.post("/auth/logout");
  },

  async requestPasswordReset(payload: ForgotPasswordRequest) {
    const requestPayload = buildForgotPasswordPayload(payload.account);
    const { data } = await api.post("/auth/forgot-password", {
      ...requestPayload,
      ...(payload.recaptchaToken
        ? { recaptcha_token: payload.recaptchaToken }
        : {}),
    });
    return buildPasswordResetChallenge(payload.account, data);
  },

  async verifyPasswordResetOtp(
    payload: ForgotPasswordVerify,
  ): Promise<PasswordResetVerification> {
    const requestPayload = buildForgotPasswordPayload(payload.account);
    const { data } = await api.post("/auth/forgot-password/verify", {
      ...requestPayload,
      otp: payload.otp,
      ...(payload.recaptchaToken
        ? { recaptcha_token: payload.recaptchaToken }
        : {}),
    });

    return {
      verified: true,
      challenge: buildPasswordResetChallenge(payload.account, data),
    };
  },

  async resetPassword(
    payload: ResetPasswordPayload,
  ): Promise<PasswordResetResult> {
    const { data } = await api.post("/auth/forgot-password/reset", {
      ...buildResetPasswordPayload(payload),
      ...(payload.recaptchaToken
        ? { recaptcha_token: payload.recaptchaToken }
        : {}),
    });
    const rawData = unwrapData<UnknownRecord>((data ?? {}) as UnknownRecord);

    return {
      message:
        typeof rawData.message === "string"
          ? rawData.message
          : "Mật khẩu đã được cập nhật.",
    };
  },

  async registerInit(payload: RegisterInitPayload) {
    const { data } = await api.post("/auth/register/init", payload);
    const rawData = unwrapData<UnknownRecord>(data as UnknownRecord);
    return {
      session_id:
        typeof rawData.session_id === "string" ? rawData.session_id : "",
    };
  },

  async registerVerify(payload: RegisterVerifyPayload) {
    const { data } = await api.post("/auth/register/verify", payload);
    return normalizeSession(data as UnknownRecord);
  },

  async setPassword(payload: SetPasswordPayload) {
    await api.post("/auth/register/set-password", payload);
  },
};
